import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9 } from "styled-components";
import React from "react";
import { AnimatePresence } from "framer-motion";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems, takeFirst } from "~/features/app/utils";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { useLinkIcon } from "~/features/ui/link/use-link-icon";
import { Headline } from "~/features/ui/headline";
import { RichText } from "~/features/ui/rich-text";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { ProductHeroLinks } from "~/features/hero/product-hero/product-hero-links";
import { ScrollSequence } from "~/features/ui/scroll-sequence";
import { ProductHeroBottomRow } from "~/features/hero/product-hero/product-hero-bottom-row";
const VIDEO_HEIGHT = 1.5; // in full viewport height units

const fragment = graphql(`
  fragment ProductHeroScrollableVideo on hero_productHeroScrollableVideo_BlockType {
    frames: baseImages {
      ... on default_Asset {
        src: url
        width
        height
      }
    }
    bottomLeftText: baseText
    bottomRightText: baseRichText
    bottomRightLink: baseLink {
      ...Link
    }
    links: productHeroLinks {
      ...ProductHeroLinks
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  title?: string;
  caption?: string;
};
export function ProductHeroScrollableVideo(props: Props) {
  const {
    title,
    caption,
    ...rest
  } = props;
  const {
    bottomLeftText,
    bottomRightText,
    links,
    bottomRightLink: _additionalLink,
    frames
  } = useFragment(fragment, rest);
  const themeStyle = getThemeStyle({
    mode: "dark"
  });
  const [hasVideoStarted, setHasVideoStarted] = React.useState(false);
  const [hasVideoPlayed, setHasVideoPlayed] = React.useState(false);
  const onProgress = React.useCallback((progress: number) => {
    setHasVideoPlayed(progress >= 1);
    setHasVideoStarted(progress > 0);
  }, []);
  const bottomRightLink = takeFirst(_additionalLink);
  const bottomRightLinkIcon = useLinkIcon(bottomRightLink);
  if (!hasItems(frames)) {
    return null;
  }
  return <_StyledDiv $_css={[{
    "borderTopWidth": "0px !important"
  }, themeStyle.background, themeStyle.text]}>
      <_StyledDiv2>
        <_StyledDiv3 style={{
        height: `${VIDEO_HEIGHT * 100}svh`
      }}>
          <ScrollSequence
        // TODO: find a better solution to make sure they come sorted
        frames={frames.sort((a, b) => a.src!.toLowerCase().localeCompare(b.src!.toLowerCase())).map(image => image.src || "")} height={VIDEO_HEIGHT} onProgress={onProgress} />
          <AnimatePresence>
            {!hasVideoStarted && <_StyledProductHeroBottomRow initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} left={<div>
                    {title && <_StyledHeadline text={title} />}
                    <_StyledP>{caption}</_StyledP>
                  </div>} right={hasItems(links) ? <ProductHeroLinks links={links} /> : undefined} />}
          </AnimatePresence>
          <AnimatePresence>
            {hasVideoPlayed && <_StyledProductHeroBottomRow2 initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} left={<div>
                    {bottomLeftText && <_StyledP2>
                        {bottomLeftText}
                      </_StyledP2>}
                  </div>} right={<div>
                    {bottomRightText && <RichText text={bottomRightText} />}
                    {bottomRightLink && <_StyledDiv4>
                        <Button as={Link} {...bottomRightLink} variant="secondary" icon={bottomRightLinkIcon} />
                      </_StyledDiv4>}
                  </div>} />}
          </AnimatePresence>
        </_StyledDiv3>
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-97611j-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-97611j-1"
})({
  "position": "relative",
  "zIndex": "10",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-97611j-2"
})({
  "display": "flex",
  "alignItems": "flex-end"
});
var _StyledProductHeroBottomRow = _styled4(ProductHeroBottomRow).withConfig({
  componentId: "sc-sf-97611j-3"
})({
  "position": "absolute",
  "top": "100svh",
  "--tw-translate-y": "-100%",
  "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
});
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-97611j-4"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "4.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.044rem",
    "fontWeight": "400"
  }
});
var _StyledP = _styled6("p").withConfig({
  componentId: "sc-sf-97611j-5"
})({
  "marginTop": "0.8rem",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "marginTop": "1.8rem",
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledProductHeroBottomRow2 = _styled7(ProductHeroBottomRow).withConfig({
  componentId: "sc-sf-97611j-6"
})({
  "position": "absolute",
  "bottom": "0"
});
var _StyledP2 = _styled8("p").withConfig({
  componentId: "sc-sf-97611j-7"
})({
  "marginTop": "0.8rem",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "marginTop": "1.8rem",
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = /*#__PURE__*/_styled9("div").withConfig({
  displayName: "product-hero-scrollable-video___StyledDiv4",
  componentId: "sc-1efh1in-0"
})({
  "marginTop": "2.4rem",
  "alignSelf": "flex-end"
});